import { useDashboard } from "../hooks/useDashboard";
import { Loading } from "../components/common/Loading";
import { Alert } from "../components/common/Alert";
import { Card } from "../components/common/Card";
import { useAuth } from "../hooks/useAuth";
import { formatText } from "../components/common/formatHelpers";

import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend
} from "recharts";

// Colores consistentes para los gráficos
const COLORS = ['#1d8cf8', '#ff8d72', '#00f269ff', '#fd5d93', '#51cbce', '#f59e0b'];

// Función personalizada para renderizar etiquetas en el gráfico de pie
const renderCustomizedLabel = ({
  cx, cy, midAngle, innerRadius, outerRadius, percent, name
}) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor={x > cx ? 'start' : 'end'}
      dominantBaseline="central"
      fontSize={12}
      fontWeight="bold"
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const reorderByCurrentBlock = (actividad = []) => {
  if (!actividad.length) return [];

  const currentHour = new Date().getHours();
  const blockIndex = Math.floor(currentHour / 4); // 0 a 5

  // rota el array para que empiece desde el bloque actual
  return [
    ...actividad.slice(blockIndex),
    ...actividad.slice(0, blockIndex)
  ];
};


export default function DashboardAdmin() {
  const { hasAdminAccess } = useAuth();
  const { stats, chartData, loading, error } = useDashboard();
  const capitalizeFirst = str => str.charAt(0).toUpperCase() + str.slice(1);

  if (loading) return <Loading text="Cargando estadísticas..." />;
  if (error) return <Alert variant="error">{error}</Alert>;
  if (!stats) return <Alert variant="warning">No hay datos disponibles</Alert>;
  if (!hasAdminAccess) {
    return (
      <Alert variant="error">
        No tienes permisos para acceder a esta sección
      </Alert>
    );
  }


  return (
    <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "20px" }}>
      <h1 style={{ fontSize: "32px", fontWeight: "700", color: "#1e293b", marginBottom: "32px" }}>
        Panel de Control
      </h1>

      {/* KPIs PRINCIPALES */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: "16px",
        marginBottom: "24px"
      }}>
        {[
          { label: "Denuncias Totales", value: stats.totalReclamos, color: "#1d8cf8" },
          { label: "Activas", value: stats.reclamosActivos, color: "#8b5cf6" },
          { label: "Usuarios Registrados", value: stats.usuariosRegistrados, color: "#0ea5e9" },
          { label: "Con Suscripción", value: stats.reclamosConSuscripcion, color: "#06b6d4" },
        ].map((item) => (
          <Card key={item.label} padding="20px">
            <div style={{ textAlign: "center" }}>
              <div style={{
                fontSize: "28px",
                fontWeight: "700",
                color: item.color,
                marginBottom: "4px"
              }}>
                {item.value}
              </div>
              <div style={{ fontSize: "14px", color: "#64748b" }}>{item.label}</div>
            </div>
          </Card>
        ))}
      </div>

      {/* GRÁFICOS PRINCIPALES */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "2fr 1fr",
        gap: "24px",
        marginBottom: "24px"
      }}>
        {/* Tendencia Semanal */}
        <Card padding="24px">
          <h2 style={{ fontSize: "18px", marginBottom: "16px", color: "#1e293b" }}>
            Tendencia de Denuncias (Últimas 4 semanas)
          </h2>
          <div style={{ width: "100%", height: 250 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData?.tendencia || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="semana" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="reclamos" fill="#00f2c3" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Indicadores de Calidad */}
        <Card padding="24px">
          <h2 style={{ fontSize: "18px", marginBottom: "16px", color: "#1e293b" }}>
            Indicadores de Calidad
          </h2>
          <div style={{ padding: "16px 0" }}>
            <div style={{ marginBottom: "16px" }}>
              <div style={{ fontSize: "14px", color: "#64748b", marginBottom: "4px" }}>
                Tiempo Promedio Resolución
              </div>
              <div style={{ fontSize: "20px", fontWeight: "700", color: "#10b981" }}>
                {stats.tiempoPromedioResolucionDias} días
              </div>
            </div>
            <div style={{ marginBottom: "16px" }}>
              <div style={{ fontSize: "14px", color: "#64748b", marginBottom: "4px" }}>
                Calificación de Soluciones
              </div>
              <div style={{ fontSize: "20px", fontWeight: "700", color: "#f59e0b" }}>
                {stats.promedioCalificacionSolucion}/5
              </div>
            </div>
            <div>
              <div style={{ fontSize: "14px", color: "#64748b", marginBottom: "4px" }}>
                Tasa de Confiabilidad
              </div>
              <div style={{ fontSize: "20px", fontWeight: "700", color: "#1d8cf8" }}>
                {stats.tasaConfiabilidadPromedio}%
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* GRÁFICOS SECUNDARIOS */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "2fr 1fr",
        gap: "24px",
        marginBottom: "32px"
      }}>
        {/* Actividad Horaria */}
        <Card padding="24px">
          <h2 style={{ fontSize: "18px", marginBottom: "16px", color: "#1e293b" }}>
            Actividad de Denuncias (Últimas 24h)
          </h2>
          <div style={{ width: "100%", height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={reorderByCurrentBlock(chartData?.actividad)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hora" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="reclamos" fill="#1d8cf8" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Distribución por Categoría - MEJORADO */}
        <Card padding="24px">
          <h2 style={{ fontSize: "18px", marginBottom: "16px", color: "#1e293b" }}>
            Denuncias por Categoría
          </h2>
          <div style={{ width: "100%", height: "300px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData?.categorias || []}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}  // Reducido para dar más espacio
                  fill="#8884d8"
                  label={renderCustomizedLabel}  // Etiquetas personalizadas
                  labelLine={false}
                >
                  {(chartData?.categorias || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value} denuncias`, 'Cantidad']} />
                <Legend
                  layout="vertical"
                  verticalAlign="middle"
                  align="right"
                  wrapperStyle={{
                    paddingLeft: "20px",
                    fontSize: "12px"
                  }}
                  formatter={(value, entry, index) => (
                    <span style={{ color: '#333', fontSize: '12px' }}>
                      {value}
                    </span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* INFORMACIÓN ADICIONAL */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "24px",
        marginBottom: "32px"
      }}>
        {/* Denuncias Recientes */}
        <Card padding="24px">
          <h2 style={{ fontSize: "18px", marginBottom: "16px", color: "#1e293b" }}>
            Denuncias Recientes
          </h2>
          <div style={{ minHeight: "200px" }}>
            {chartData?.denunciasRecientes?.length > 0 ? (
              <div>
                {chartData.denunciasRecientes.map(denuncia => (
                  <div key={denuncia.id} style={{
                    padding: "12px",
                    borderBottom: "1px solid #e2e8f0",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}>
                    <div>
                      <div style={{ fontWeight: "600" }}>#{denuncia.id} - {capitalizeFirst(denuncia.categoria)}</div>
                      <div style={{ fontSize: "14px", color: "#64748b" }}>{denuncia.fechacreacion}</div>
                    </div>
                    <span style={{
                      padding: "4px 12px",
                      borderRadius: "20px",
                      fontSize: "12px",
                      fontWeight: "600",
                      backgroundColor: denuncia.estado === 'Resuelto' ? '#dcfce7' :
                        denuncia.estado === 'Pendiente' ? '#fef3c7' :
                          denuncia.estado === 'En_progreso' ? '#dbeafe' : '#f3f4f6',
                      color: denuncia.estado === 'Resuelto' ? '#166534' :
                        denuncia.estado === 'Pendiente' ? '#92400e' :
                          denuncia.estado === 'En_progreso' ? '#1e40af' : '#6b7280'
                    }}>
                      {formatText(denuncia.estado)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: "center", color: "#64748b", padding: "40px 0" }}>
                No hay denuncias recientes
              </div>
            )}
          </div>
        </Card>

        {/* Distribución por Estado - MEJORADO */}
        <Card padding="24px">
          <h2 style={{ fontSize: "18px", marginBottom: "16px", color: "#1e293b" }}>
            Distribución por Estado
          </h2>
          <div style={{ width: "100%", height: "250px" }}> {/* ⬆️ aumentado de 200 a 250 */}
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData?.estados || []}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}  // ⬆️ aumentado de 60 a 80
                  fill="#8884d8"
                  label={renderCustomizedLabel}
                  labelLine={false}
                >
                  {(chartData?.estados || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value} denuncias`, "Cantidad"]} />
                <Legend
                  layout="vertical"
                  verticalAlign="middle"
                  align="right"
                  wrapperStyle={{
                    paddingLeft: "20px",  // ⬆️ más espacio
                    fontSize: "12px",
                  }}
                  formatter={(value) => (
                    <span style={{ color: "#333", fontSize: "12px" }}>{value}</span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </div>
  );
}